import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useStore } from "@/src/contexts/StoreContext";
import { cn } from "@/src/lib/utils";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckout: () => void;
}

export function CartDrawer({ isOpen, onClose, onCheckout }: CartDrawerProps) {
  const { cart, removeFromCart, updateCartQuantity, getCartTotal, formatPrice, clearCart } = useStore();

  const subtotal = getCartTotal();
  const shipping = subtotal > 5000 ? 0 : 999;
  const tax = Math.round(subtotal * 0.08);
  const total = subtotal + shipping + tax;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0a0a0a] border-l border-white/10 z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-5 h-5 text-white/60" />
                <h2 className="text-xl font-bold text-white">Your Cart</h2>
                <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs font-medium text-white/60">
                  {cart.length} items
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-white/40 hover:text-white transition-colors rounded-lg hover:bg-white/5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-16 h-16 text-white/10 mx-auto mb-4" />
                  <p className="text-white/40">Your cart is empty</p>
                  <p className="text-sm text-white/30 mt-2">Add some memories to get started!</p>
                </div>
              ) : (
                cart.map((item, index) => {
                  const price = item.product.base_price + item.variant.price_modifier;
                  return (
                    <motion.div
                      key={`${item.product.id}-${item.variant.id}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex gap-4 p-4 bg-white/[0.02] rounded-xl border border-white/5"
                    >
                      {/* Image Preview */}
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-black/50 flex-shrink-0">
                        <img
                          src={item.image_url}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white truncate">{item.product.name}</h4>
                        <p className="text-sm text-white/50">{item.variant.variant_name}</p>
                        <p className="text-sm font-medium text-white mt-1">{formatPrice(price)}</p>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            onClick={() =>
                              updateCartQuantity(item.product.id, item.variant.id, item.quantity - 1)
                            }
                            className="p-1 rounded bg-white/5 text-white/60 hover:bg-white/10"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm font-medium text-white w-8 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateCartQuantity(item.product.id, item.variant.id, item.quantity + 1)
                            }
                            className="p-1 rounded bg-white/5 text-white/60 hover:bg-white/10"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      {/* Remove & Total */}
                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => removeFromCart(item.product.id, item.variant.id)}
                          className="p-2 text-white/30 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <span className="font-semibold text-white">{formatPrice(price * item.quantity)}</span>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
              <div className="border-t border-white/5 p-6 space-y-4">
                {/* Summary */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-white/60">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-white/60">
                    <span>Shipping</span>
                    <span>{shipping === 0 ? "Free" : formatPrice(shipping)}</span>
                  </div>
                  <div className="flex justify-between text-white/60">
                    <span>Tax (8%)</span>
                    <span>{formatPrice(tax)}</span>
                  </div>
                  {subtotal < 5000 && (
                    <p className="text-xs text-white/40 text-center pt-1">
                      Add {formatPrice(5000 - subtotal)} more for free shipping!
                    </p>
                  )}
                  <div className="flex justify-between text-white font-semibold text-lg pt-2 border-t border-white/5">
                    <span>Total</span>
                    <span>{formatPrice(total)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button
                    onClick={onCheckout}
                    className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:opacity-90 transition-all"
                  >
                    Proceed to Checkout
                  </button>
                  <button
                    onClick={clearCart}
                    className="w-full py-3 text-white/40 text-sm hover:text-white transition-colors"
                  >
                    Clear Cart
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}